import { useState, useEffect } from "react";
import { toast } from "sonner";
import { X, ArrowUpRight } from "@phosphor-icons/react";
import { submitOrder } from "@/lib/api";
import { BUSINESS } from "@/lib/data";

const field =
  "w-full rounded-xl border border-slate-300 bg-white px-4 py-3 text-navy-900 placeholder:text-slate-400 focus:outline-none focus:ring-2 focus:ring-navy-600 focus:border-navy-600 transition";
const label = "block text-sm font-semibold text-navy-900 mb-1.5";

export const OrderModal = ({ product, onClose }) => {
  const [loading, setLoading] = useState(false);
  const [form, setForm] = useState({ name: "", email: "", phone: "", quantity: 1, note: "" });
  const isService = product.category === "Service Package";
  const isGiftCard = product.category === "Gift Card";

  useEffect(() => {
    const onKey = (e) => e.key === "Escape" && onClose();
    window.addEventListener("keydown", onKey);
    document.body.style.overflow = "hidden";
    return () => {
      window.removeEventListener("keydown", onKey);
      document.body.style.overflow = "";
    };
  }, [onClose]);

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await submitOrder({
        product: product.name,
        price: product.price || "",
        category: product.category || "",
        ...form,
        quantity: Number(form.quantity) || 1,
      });
      toast.success(
        isService
          ? "Service booked! We'll email you to confirm timing & details."
          : isGiftCard
          ? "Gift card ordered! We'll email your digital card once payment is arranged."
          : "Order placed! We'll email you to arrange payment & pickup."
      );
      onClose();
    } catch {
      toast.error(`Something went wrong. Please call us at ${BUSINESS.phone}.`);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div
      className="fixed inset-0 z-[70] bg-navy-900/60 backdrop-blur-sm flex items-center justify-center p-4"
      onMouseDown={onClose}
      data-testid="order-modal"
    >
      <div
        className="bg-white rounded-3xl w-full max-w-md p-6 md:p-8 relative max-h-[90vh] overflow-y-auto"
        onMouseDown={(e) => e.stopPropagation()}
      >
        <button
          onClick={onClose}
          data-testid="order-modal-close"
          aria-label="Close"
          className="absolute top-5 right-5 w-9 h-9 grid place-items-center rounded-full border border-slate-300 text-navy-900 hover:bg-slate-100"
        >
          <X size={18} />
        </button>

        <span className="text-xs font-bold uppercase tracking-[0.2em] text-navy-600">
          {isService ? "Book this service" : "Place an order"}
        </span>
        <h3 className="font-display font-black text-2xl text-navy-900 tracking-tight mt-2">{product.name}</h3>
        {product.price && <p className="text-slate-500 mt-1 font-semibold">{product.price}</p>}
        <p className="text-slate-500 text-sm mt-3">
          {isService
            ? "Tell us how to reach you and we'll confirm the timing & details of your job. No online payment required."
            : isGiftCard
            ? "Your gift card is delivered digitally. Enter the recipient's email below and we'll send the card straight to their inbox once payment is arranged."
            : "Tell us how to reach you and we'll confirm payment & pickup/delivery. No online payment required yet."}
        </p>

        <form onSubmit={submit} data-testid="order-form" className="mt-5 space-y-4">
          <div className="grid grid-cols-3 gap-3">
            <div className="col-span-2">
              <label className={label}>Full name</label>
              <input required data-testid="order-name" className={field} placeholder="Jane Smith"
                value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} />
            </div>
            <div>
              <label className={label}>{isService ? "Hrs" : "Qty"}</label>
              <input type="number" min="1" data-testid="order-qty" className={field}
                value={form.quantity} onChange={(e) => setForm({ ...form, quantity: e.target.value })} />
            </div>
          </div>
          <div>
            <label className={label}>{isGiftCard ? "Recipient email (where we'll send the card)" : "Email"}</label>
            <input required type="email" data-testid="order-email" className={field} placeholder="you@email.com"
              value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} />
          </div>
          <div>
            <label className={label}>Phone</label>
            <input required data-testid="order-phone" className={field} placeholder="(843) 000-0000"
              value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} />
          </div>
          <div>
            <label className={label}>
              {isService ? "Job details / what needs doing?" : isGiftCard ? "Gift message for the recipient (optional)" : "Note (size, color, details…)"}
            </label>
            <textarea rows={3} data-testid="order-note" className={field}
              placeholder={isService ? "Describe the work, location & any specifics…" : isGiftCard ? "Add a personal note we'll include with the gift card…" : "Anything we should know?"}
              value={form.note} onChange={(e) => setForm({ ...form, note: e.target.value })} />
          </div>
          <button
            type="submit"
            disabled={loading}
            data-testid="order-submit"
            className="w-full inline-flex items-center justify-center gap-2 rounded-full bg-navy-900 text-white py-3.5 font-bold hover:bg-navy-600 transition-colors disabled:opacity-60"
          >
            {isService
              ? loading ? "Booking…" : "Book this service"
              : loading ? "Placing order…" : "Place order"} <ArrowUpRight weight="bold" />
          </button>
        </form>
      </div>
    </div>
  );
};
